import React, { useEffect } from 'react';
import { useNavigate} from "react-router-dom";
import defaultImage from "../../assets/images/default_dashboard_img.jpg";
import { checkMqttCard, getLevelColor, getCardDeviceType, DEVICE_TYPES, DISCRETE_UPDATE_CADENCE } from "../../utils/cardUtils";
import PropTypes from "prop-types";
import { useCardData, useCardContext } from '../common/CardContext';
import { Sparklines, SparklinesLine, SparklinesSpots } from 'react-sparklines';
import { useSparklineData } from '../../hooks/useSparklineData';
import { processTimeStamp } from '../../utils/chartUtils';
import { useNow } from '../../hooks/useNow';
import { formatAge } from '../../utils/timeUtils';

// A device is considered online if we received a live packet within this window.
const ONLINE_WINDOW_MS = 5 * 60 * 1000;

// Sparkline display component for showing 24-hour trends
function SparklineDisplay({ macAddress, sensorType, color, height, width }) {
    const { data, loading } = useSparklineData(macAddress, sensorType);
    
    if (loading || data.length === 0) {
        return (
            <div className="sparkline-container" style={{ height, width }}>
                <div className="sparkline-placeholder"></div>
            </div>
        );
    }
    
    return (
        <div className="sparkline-container">
            <Sparklines data={data} width={width} height={height} margin={2}>
                <SparklinesLine color={color} style={{ fillOpacity: 0.3 }} />
                <SparklinesSpots size={1} style={{ fill: color }} />
            </Sparklines>
        </div>
    );
}

export default function Card({sensor, onDelete}) {
    const navigate = useNavigate();
    const { cardData, initializeCardData } = useCardData();
    const deviceType = getCardDeviceType(sensor?.macAddress);
    const isDiscreteCard = deviceType === DEVICE_TYPES.DISCRETE;
    const isMqttCard = checkMqttCard(sensor?.macAddress);
    
    // Helper function to check if a value is valid (not null, undefined, empty string, or invalid)
    const isValidValue = (value) => {
        if (value === null || value === undefined) return false;
        if (value === '') return false;
        if (typeof value === 'string' && value.trim() === '') return false;
        // Convert to number and check if it's a valid number
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        return !isNaN(numValue) && isFinite(numValue);
    };
    
    // Get the latest MQTT data from context if this is an MQTT card
    const latestData = isMqttCard ? cardData[sensor.macAddress] : null;
    
    // Check if we have data in CardContext
    const hasContextData = latestData !== null && latestData !== undefined;
    
    // Get raw values - for MQTT cards, prioritize context data, then fall back to sensor backend data
    // This ensures we show the last stored values immediately if context doesn't have data yet
    const rawTempValue = isMqttCard 
        ? (latestData?.mostRecentTemp ?? sensor?.mostRecentTemp ?? null)
        : (latestData?.mostRecentTemp ?? sensor.mostRecentTemp ?? 0.00);
    const rawHumidityValue = isMqttCard
        ? (latestData?.mostRecentHumidity ?? sensor?.mostRecentHumidity ?? null)
        : (latestData?.mostRecentHumidity ?? sensor.mostRecentHumidity ?? 0.00);
    
    // Check if we have valid data from context
    const hasContextTemp = isValidValue(latestData?.mostRecentTemp);
    const hasContextHumidity = isValidValue(latestData?.mostRecentHumidity);
    
    // Check if we have valid data from sensor backend
    const hasSensorTemp = isValidValue(sensor?.mostRecentTemp);
    const hasSensorHumidity = isValidValue(sensor?.mostRecentHumidity);
    
    // We have data if we have it from context OR from sensor backend
    const hasTempData = hasContextTemp || hasSensorTemp;
    const hasHumidityData = hasContextHumidity || hasSensorHumidity;
    
    // Convert to numbers for display - use the value we have (context first, then sensor backend)
    // For MQTT cards: if we have data, use it; if not, we'll show loading dots (so value doesn't matter)
    // For non-MQTT cards: default to 0.00
    const currentTemp = hasTempData 
        ? (typeof rawTempValue === 'string' ? parseFloat(rawTempValue) : rawTempValue)
        : 0.00;
    const currentHumidity = hasHumidityData 
        ? (typeof rawHumidityValue === 'string' ? parseFloat(rawHumidityValue) : rawHumidityValue)
        : 0.00;
    // Display timestamp: the last known formatted time from live context or backend.
    // IMPORTANT: do NOT fall back to now() — an absent timestamp means "never reported",
    // not "reported just now". Freshness/online is derived from mostRecentEpochMs below.
    const displayTimestamp = latestData?.mostRecentTimestamp ?? sensor?.mostRecentTimestamp ?? null;
    
    // Determine if we should show loading state (dashes with gray)
    // For MQTT cards: show loading ONLY if we have NO valid data from either context OR sensor backend
    // This means we immediately show the last stored values from sensor backend if context is empty
    const isLoadingTemp = isMqttCard && !hasTempData;
    const isLoadingHumidity = isMqttCard && !hasHumidityData;

    // Note: We no longer need to update React Query cache directly here
    // The CardContext now handles this with time-based and significance-based persistence
    // This component simply uses the real-time data from context for display
    // and CardContext handles when to persist to the backend
    
    // If this is an MQTT card that's not yet in the context, initialize it with sensor data
    useEffect(() => {
        // Check if we already have context data for this MAC address
        const hasContextDataNow = cardData[sensor?.macAddress] !== null && cardData[sensor?.macAddress] !== undefined;
        
        if (isMqttCard && sensor?.macAddress && !hasContextDataNow && initializeCardData) {
            // Check if we have valid sensor data to initialize with
            const hasValidTemp = isValidValue(sensor.mostRecentTemp);
            const hasValidHumidity = isValidValue(sensor.mostRecentHumidity);
            const hasSensorData = hasValidTemp || hasValidHumidity;
            
            if (hasSensorData) {
                // Initialize the card data in context with backend sensor values
                // Only include values that are valid
                const initData = {};
                if (hasValidTemp) {
                    initData.mostRecentTemp = typeof sensor.mostRecentTemp === 'string' 
                        ? parseFloat(sensor.mostRecentTemp) 
                        : sensor.mostRecentTemp;
                }
                if (hasValidHumidity) {
                    initData.mostRecentHumidity = typeof sensor.mostRecentHumidity === 'string' 
                        ? parseFloat(sensor.mostRecentHumidity) 
                        : sensor.mostRecentHumidity;
                }
                if (isValidValue(sensor.mostRecentMoisture)) {
                    initData.mostRecentMoisture = typeof sensor.mostRecentMoisture === 'string' 
                        ? parseFloat(sensor.mostRecentMoisture) 
                        : sensor.mostRecentMoisture;
                }
                if (isValidValue(sensor.mostRecentDistance)) {
                    initData.mostRecentDistance = typeof sensor.mostRecentDistance === 'string' 
                        ? parseFloat(sensor.mostRecentDistance) 
                        : sensor.mostRecentDistance;
                }
                if (isValidValue(sensor.mostRecentLight)) {
                    initData.mostRecentLight = typeof sensor.mostRecentLight === 'string' 
                        ? parseFloat(sensor.mostRecentLight) 
                        : sensor.mostRecentLight;
                }
                if (sensor.mostRecentTimestamp) {
                    initData.mostRecentTimestamp = sensor.mostRecentTimestamp;
                }
                
                console.log('Card: Initializing CardContext with sensor data:', {
                    macAddress: sensor.macAddress,
                    initData
                });
                
                initializeCardData(sensor.macAddress, initData);
            }
        }
    }, [sensor?.macAddress, sensor?.mostRecentTemp, sensor?.mostRecentHumidity, isMqttCard, cardData, initializeCardData]);

    const handleCardClick = () => {
        if (isDiscreteCard) {
            navigate(`/charts/discrete/${sensor.macAddress}`, {
                state: { card: sensor }
            });
            return;
        }
        if (isMqttCard) {
            navigate(`/charts/mqtt/${sensor.macAddress}`, {
                state: { card: sensor }
            });
            return;
        }
        navigate(`/charts/${sensor.macAddress}`, { 
            state: { card: sensor }  
        });
    };
    
    const handleDeleteClick = (e) => {
        e.stopPropagation(); 
        onDelete(sensor.macAddress);
    };

    const batteryLevel = Math.max(0, Math.min(1, (parseFloat(sensor.mostRecentBattery) - 3.5) / (4.2 - 3.5))) * 100;
    const borderStyle = { "--battery-level": `${batteryLevel}%` };

    // Ticking clock so online/offline + "last seen" re-evaluate on their own, even if no
    // other state changes (a silent/unplugged device must eventually flip to offline).
    const now = useNow(30000);

    // Freshness is based on when we last received a LIVE packet this session
    // (mostRecentEpochMs), not on a parsed display string. If we only have backend-seeded
    // values and no live packet yet, age is unknown → treat as not-online / "last known".
    const liveEpochMs = latestData?.mostRecentEpochMs ?? null;
    const ageMs = liveEpochMs != null ? now - liveEpochMs : null;

    // Only MQTT cards have a live stream; online requires a fresh live packet.
    const isOnline = isMqttCard && ageMs != null && ageMs < ONLINE_WINDOW_MS;

    // Stale = MQTT card that has values to show but is not currently online (silent device,
    // or just-reloaded before the first live packet). Values are dimmed and labeled.
    const isStale = isMqttCard && !isOnline;
    const lastSeenLabel = formatAge(ageMs);

    return (
        <div 
            className={"card " + 
                (isDiscreteCard ? "card-discrete " : "") +
                (isMqttCard ? "card-mqtt " : "") + 
                (isMqttCard ? (isOnline ? "online" : (ageMs != null ? "offline" : "")) : "")
            } 
            onClick={handleCardClick} 
            style={borderStyle}
        >
            <div className="card-header">
                <h3 className="card-description">
                    {sensor.sensorDescription}
                </h3>
                {sensor.macAddress && (
                    <p className="card-mac-address" title={sensor.macAddress}>
                        {sensor.macAddress}
                    </p>
                )}
            </div>

            {isDiscreteCard && (
                <span className="card-discrete-badge" title="Discrete LoRa sensor — periodic updates">
                    <i className="ri-base-station-line"></i>
                    {DISCRETE_UPDATE_CADENCE}
                </span>
            )}

            <div className="card-image-container">
                <img src={sensor.cardImage || defaultImage} alt={sensor.sensorDescription || "Sensor Image"} className="card-image" />
            </div>

            <div className="card-data">
                <div className="card-humidity-data">
                    <p 
                        className="card-paragraph humidity-data"
                        style={{
                            color: isLoadingHumidity 
                                ? 'rgba(255, 255, 255, 0.4)' 
                                : isStale
                                    ? 'rgba(255, 255, 255, 0.45)'
                                    : getLevelColor(currentHumidity, 0, 90)
                        }}
                    >
                        {isLoadingHumidity ? (
                            <>
                                <span className="dash dash-1">-</span>
                                <span className="dash dash-2">-</span>
                            </>
                        ) : (
                            `${currentHumidity}%`
                        )}
                    </p>
                    <SparklineDisplay 
                        macAddress={sensor.macAddress} 
                        sensorType="humidity" 
                        color="#4ade80" /* Glowing green color for humidity */
                        height={20} 
                        width={80} 
                    />
                    <div className="thresholds-container">
                        <div className="threshold-item">
                            <span className="threshold-low">↓</span>
                            <span>{sensor.humidityLow !== "" ? `${sensor.humidityLow}%` : "--"}</span>
                        </div>
                        <div className="threshold-item">
                            <span className="threshold-high">↑</span>
                            <span>{sensor.humidityHigh !== "" ? `${sensor.humidityHigh}%` : "--"}</span>
                        </div>
                    </div>
                </div>

                <div className="card-temp-data">
                    <p 
                        className="card-paragraph temp-data"
                        style={{
                            color: isLoadingTemp 
                                ? 'rgba(255, 255, 255, 0.4)' 
                                : isStale
                                    ? 'rgba(255, 255, 255, 0.45)'
                                    : getLevelColor(currentTemp, 0, 100)
                        }}
                    >
                        {isLoadingTemp ? (
                            <>
                                <span className="dash dash-1">-</span>
                                <span className="dash dash-2">-</span>
                            </>
                        ) : (
                            `${currentTemp} °F`
                        )}
                    </p>
                    <SparklineDisplay 
                        macAddress={sensor.macAddress} 
                        sensorType="temperature" 
                        color="#ff9a3c" /* Glowing orange color for temperature */
                        height={20} 
                        width={80} 
                    />
                    <div className="thresholds-container">
                        <div className="threshold-item">
                            <span className="threshold-low">↓</span>
                            <span>{sensor.temperatureLow !== "" ? `${sensor.temperatureLow}°F` : "--"}</span>
                        </div>
                        <div className="threshold-item">
                            <span className="threshold-high">↑</span>
                            <span>{sensor.temperatureHigh !== "" ? `${sensor.temperatureHigh}°F` : "--"}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card-timestamp">
                {isMqttCard && (
                    <div
                        className={"card-status-indicator " + (
                            isOnline
                                ? "card-status-online"
                                : ageMs != null
                                    ? "card-status-offline"
                                    : "card-status-unknown"
                        )}
                        title={displayTimestamp ? `Last reading: ${displayTimestamp}` : "No reading received yet"}
                    >
                        <span className="card-status-dot" aria-hidden="true"></span>
                        {isOnline
                            ? `Online · ${lastSeenLabel || 'just now'}`
                            : ageMs != null
                                ? `Offline · last seen ${lastSeenLabel}`
                                : "Last known reading"}
                    </div>
                )}
                <div className="card-paragraph-smaller">{displayTimestamp ?? "—"}</div>
            </div>

            {/*Delete Button*/}
            <button className="card-delete-button" onClick={handleDeleteClick}>
                <i className="ri-close-large-line custom-close-icon"></i>
            </button>
        </div>
    );
}

// Prop validation
Card.propTypes = {
    sensor: PropTypes.shape({
        macAddress: PropTypes.string,
        sensorDescription: PropTypes.string,
        addIrrigation: PropTypes.string,
        humidityHigh: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        humidityLow: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        temperatureHigh: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        temperatureLow: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        waterHigh: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        waterLow: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        cardImage: PropTypes.oneOfType([PropTypes.string]),
        mostRecentBattery: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        mostRecentTemp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), 
        mostRecentHumidity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), 
        mostRecentTimestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        mostRecentWater: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        lightLow: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        lightHigh: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    onDelete: PropTypes.func.isRequired,
};

// Prop validation for SparklineDisplay component
SparklineDisplay.propTypes = {
    macAddress: PropTypes.string.isRequired,
    sensorType: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    height: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired
};